// src/pages/AccessControlPage.tsx

import React, { useEffect, useMemo, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";

import UsersTable from "@/components/admin/access-control/UsersTable";
import Permissions from "@/components/admin/access-control/Permissions";

import {
  getAccessRole,
  getCompanyAdmins,
  inviteCompanyAdmin,
  updateCompanyAdmin,
  deleteAdminUser,
  upsertAccessRole,
  deleteRoleById,
  mergeEmployeeLogin,
  grantEmployeeAdminAccess,
} from "@/services/adminService";

import { getErrorMessage } from "@/utils/errorHandler";
import { useAuth } from "@/contexts/AuthContext";

export default function AccessControlPage() {
  const { logout } = useAuth();
  /* ================= USERS STATE ================= */
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  /* ================= ROLES STATE ================= */
  const [roles, setRoles] = useState([]);
  const [loadingRoles, setLoadingRoles] = useState(false);

  const rolesById = useMemo(() => {
    const map = new Map();
    roles.forEach((r) => map.set(String(r._id ?? r.id), r));
    return map;
  }, [roles]);

  /* ================= FETCH ROLES ================= */
  const fetchRoles = async () => {
    try {
      setLoadingRoles(true);
      const res = await getAccessRole();
      setRoles(Array.isArray(res) ? res : []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Failed to load roles"));
    } finally {
      setLoadingRoles(false);
    }
  };

  /* ================= FETCH USERS ================= */
  const fetchUsers = async () => {
    try {
      setLoadingUsers(true);
      const res = await getCompanyAdmins();
      setUsers(Array.isArray(res) ? res : []);
    } catch (e) {
      toast.error(getErrorMessage(e, "Failed to load users"));
    } finally {
      setLoadingUsers(false);
    }
  };

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, []);

  /* ================= USER CRUD ================= */
  // Deactivating or deleting a manager unassigns their direct reports (backend).
  const unassignedNote = (res) => {
    const n = Number(res?.unassigned_reports || 0);
    return n > 0
      ? ` Removed as manager from ${n} employee${n === 1 ? "" : "s"}.`
      : "";
  };

  const handleUpsertUser = async (payload) => {
    try {
      if (payload?.grantAdminAccess) {
        // An existing employee: their own login gets admin access.
        await grantEmployeeAdminAccess(
          payload.grantAdminAccess.employee_id,
          payload.grantAdminAccess.roleId,
        );
        toast.success("Admin access given. They'll see the admin sections the next time they sign in.");
      } else if (payload?.id && payload.merge) {
        // One login per person: merge first, then save the role / status.
        // Email and name come from the sign-in they kept.
        const merged = await mergeEmployeeLogin(payload.id, payload.merge);
        const res = await updateCompanyAdmin({
          id: payload.id,
          roleId: payload.roleId,
          status: payload.status,
        });
        toast.success(`Logins merged. They now sign in with ${merged?.email}.${unassignedNote(res)}`);
        if (merged?.signed_out) {
          toast.info("You merged your own login onto another email. Please sign in again.");
          await logout();
          return;
        }
      } else if (payload?.id) {
        const res = await updateCompanyAdmin(payload);
        toast.success(`User updated successfully.${unassignedNote(res)}`);
      } else {
        await inviteCompanyAdmin(payload);
        toast.success("Invitation sent successfully");
      }

      await fetchUsers();
    } catch (e) {
      toast.error(getErrorMessage(e, "Failed to save user"));
    }
  };

  const handleDeleteUser = async (user) => {
    try {
      const res = await deleteAdminUser(user._id);
      toast.success(
        res?.admin_access_removed
          ? `Admin access removed. They keep their employee login.${unassignedNote(res)}`
          : `User deleted.${unassignedNote(res)}`,
      );
      await fetchUsers();
    } catch (e) {
      toast.error(getErrorMessage(e, "Failed to delete user"));
    }
  };

  /* ================= ROLE CRUD ================= */

  const handleSaveRole = async (payload) => {
    try {
      await upsertAccessRole(payload);
      await fetchRoles();
    } catch (e) {
      throw e;
    }
  };

  const handleDeleteRole = async (roleId) => {
    try {
      await deleteRoleById(roleId);
      await fetchRoles();
    } catch (e) {
      toast.error(getErrorMessage(e, "Failed to delete role"));
    }
  };

  /* ================= RENDER ================= */
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
          Access Control
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-1">
          Manage users and role-based permissions (RBAC)
        </p>
      </div>

      <Tabs defaultValue="users" className="w-full">
          <TabsList className="flex justify-start space-x-8 overflow-x-auto bg-transparent h-auto p-0 no-scrollbar">
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="roles">Roles</TabsTrigger>
          </TabsList>

        <TabsContent value="users" className="mt-8">
          <UsersTable
            users={users}
            roles={roles}
            rolesById={rolesById}
            loading={loadingUsers || loadingRoles}
            onUpsertUser={handleUpsertUser}
            onDeleteUser={handleDeleteUser}
          />
        </TabsContent>

        <TabsContent value="roles" className="mt-8">
          <Permissions
            roles={roles}
            loading={loadingRoles}
            onSaveRole={handleSaveRole}
            onDeleteRole={handleDeleteRole}
            onRefresh={fetchRoles}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
